import React, { useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../store/ContextStore";
import api from "../utils/api";
import { GAME_DASHBOARD, GAME_PLAYER_STATS, GAME_TEAM_STATS, GAME_ROSTERS,GAME_PLAY_BY_PLAY, GAME_SUMMARY } from "../constants/routes";
import TeamStatsTable from "../components/TeamStatsTable";
import PlayerTeamStatsTable from "../components/PlayerTeamStatsTable";
import RostersTable from "../components/RostersTable";
import { useReportIssue } from '../hooks/useReportIssue';
import { ReportIssueModal } from '../components/ReportIssueModal';

interface GameDashboardData {
  game_date: string;
  team_name: string;
  team_code: number;
  opponent_team_name: string;
  opponent_team_code: number;
  team_score: number;
  opponent_score: number;
  venue: string;
  attendance: number;
  season: number;
  categories: string[];
}

interface PlayerStats {
  [key: string]: number;
}

interface Player {
  player_name: string;
  position: string;
  stats: PlayerStats;
}

interface StatsData {
  home_team_name: string;
  opp_team_name: string;
  home_team_player_stats: Player[];
  opp_team_player_stats: Player[];
}

interface PlayByPlayEvent {
  id: number;
  team_id: number;
  action_type: string;
  team_name: string;
  clock: string;
  period: number;
  period_type: string;
  text: string;
  time_actual: string;
  value?: number;
  created_at: string;
  updated_at: string;
  clock_display_message?: string;
}

interface PlayByPlayResponse {
  play_by_play: PlayByPlayEvent[];
  clock_display_message?: string;
}

 type TabCode = "player-stats" | "team-stats" | "rosters" | "play-by-play" | "summary";

interface GameSummary {
  summary: string;
  match_id: string;
  sport_code: string;
}

const GameDashboard = () => {
  const [activeTab, setActiveTab] = useState<TabCode>("player-stats");
  const [gameData, setGameData] = useState<GameDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const { team } = useContext(AuthContext);
  const { sportsCode, matchId, teamcode } = useParams<{ sportsCode: string; matchId: string, teamcode: string }>();

  // --- Player Stats Tab State ---
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [statsData, setStatsData] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsErrorStatus, setStatsErrorStatus] = useState<number | null>(null);
  const [statKeys, setStatKeys] = useState<string[]>([]);

  // Team Stats state
  const [teamStatsData, setTeamStatsData] = useState<any>(null);
  const [teamStatsLoading, setTeamStatsLoading] = useState(false);
  const [teamStatsError, setTeamStatsError] = useState<string | null>(null);
  const [teamStatsErrorStatus, setTeamStatsErrorStatus] = useState<number | null>(null);

  // Rosters state
  const [rostersData, setRostersData] = useState<any>(null);
  const [rostersLoading, setRostersLoading] = useState(false);
  const [rostersError, setRostersError] = useState<string | null>(null);
  const [rostersErrorStatus, setRostersErrorStatus] = useState<number | null>(null);
  const [rostersStatus, setRostersStatus] = useState<Record<string, boolean>>({});
  const [showStartersOnly, setShowStartersOnly] = useState<boolean>(false);

  // Play-by-Play State
  const [playByPlayData, setPlayByPlayData] = useState<PlayByPlayEvent[]>([]);
  const [playByPlayLoading, setPlayByPlayLoading] = useState(false);
  const [playByPlayError, setPlayByPlayError] = useState<string | null>(null);
  const [playByPlayErrorStatus, setPlayByPlayErrorStatus] = useState<number | null>(null);
  const [clockDisplayMessage, setClockDisplayMessage] = useState<string>('show_clock');

  // Story/Game Summary State
  const [storyData, setStoryData] = useState<GameSummary | null>(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [storyErrorStatus, setStoryErrorStatus] = useState<number | null>(null);

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | 'none'>('none');

  // Report Issue Hook
  const reportIssue = useReportIssue();

  // Handle Report Issue
  const handleReportIssue = (errorContext?: string) => {
    console.log('Report issue clicked!');
    reportIssue.openModal({
      ComponentSource: 'GameDashboard',
      ErrorContext: errorContext || '',
      UserContext: {
        sportsCode,
        matchId,
        teamcode,
        activeTab,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      },
    });
  };

  const handleStatusChange = async (playerId: string, starter: boolean) => {
    setRostersStatus(prev => ({ ...prev, [playerId]: starter }));
    try {
      await api.post(GAME_ROSTERS, {
        SportCode: sportsCode?.toUpperCase(),
        Match_id: matchId,
        HomeTeamCode: teamcode,
        PlayerId: playerId,
        Starter: starter,
      });
    } catch (err: any) {
      setRostersError("Failed to update starter status.");
      setRostersErrorStatus(err?.response?.status ?? 500);
      setRostersStatus(prev => ({ ...prev, [playerId]: !starter }));
    }
  };

  // Filter players with non-zero stats
  const filterPlayersWithNonZeroStats = (players: Player[]): Player[] => {
    return players.filter(player => {
      if (!player.stats || typeof player.stats !== 'object') {
        return false;
      }
      return Object.values(player.stats).some(statValue => {
        const numValue = Number(statValue);
        return !isNaN(numValue) && numValue !== 0;
      });
    });
  };

  // Fetch player stats with filtering
  useEffect(() => {
    if (activeTab !== "player-stats" || !selectedCategory) return;
    
    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError(null);
      setStatsErrorStatus(null);
      
      try {
        const resp = await api.get(GAME_PLAYER_STATS, {
          params: {
            SportCode: sportsCode?.toUpperCase(),
            Match_id: matchId,
            HomeTeamCode: teamcode,
            category: selectedCategory,
          },
        });
        
        const filteredData: StatsData = {
          ...resp.data,
          home_team_player_stats: filterPlayersWithNonZeroStats(resp.data.home_team_player_stats || []),
          opp_team_player_stats: filterPlayersWithNonZeroStats(resp.data.opp_team_player_stats || [])
        };
        
        setStatsData(filteredData);
        
        const homeStats = filteredData.home_team_player_stats || [];
        const oppStats = filteredData.opp_team_player_stats || [];
        const allStatKeys = new Set<string>();
        [...homeStats, ...oppStats].forEach((player) => {
          if (player.stats) {
            Object.keys(player.stats).forEach((key) => allStatKeys.add(key));
          }
        });
        setStatKeys(Array.from(allStatKeys));
      } catch (err: any) {
        setStatsError("Failed to load player stats.");
        setStatsErrorStatus(err?.response?.status ?? 500);
        setStatsData(null);
      } finally {
        setStatsLoading(false);
      }
    };
    
    fetchStats();
  }, [activeTab, selectedCategory, sportsCode, matchId, teamcode]);

  // Set default category when gameData is loaded
  useEffect(() => {
    if (gameData && gameData.categories && gameData.categories.length > 0) {
      setSelectedCategory(gameData.categories[0]);
    }
  }, [gameData]);

  useEffect(() => {
    const fetchGameData = async () => {
      if (!sportsCode || !matchId) {
        setError("Missing required parameters");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await api.post(GAME_DASHBOARD, {
          SportCode: sportsCode.toUpperCase(),
          Match_id: matchId,
          HomeTeamCode: teamcode
        });
        setGameData(response.data);
        setError(null);
        setErrorStatus(null);
      } catch (err: any) {
        console.error("Error fetching game data:", err);
        setError("Failed to load game data. Please try again later.");
        setErrorStatus(err?.response?.status ?? 500);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGameData();
  }, [sportsCode, matchId, teamcode]);

  // Fetch team stats when Team Stats tab is active
  useEffect(() => {
    if (activeTab !== "team-stats") return;
    const fetchTeamStats = async () => {
      setTeamStatsLoading(true);
      setTeamStatsError(null);
      setTeamStatsErrorStatus(null);
      try {
        const resp = await api.get(GAME_TEAM_STATS, {
          params: { SportCode: sportsCode?.toUpperCase(), Match_id: matchId, HomeTeamCode: teamcode }
        });
        console.log("Team Stats Data:", resp.data);
        setTeamStatsData(resp.data);
      } catch (err: any) {
        setTeamStatsError("Failed to load team stats.");
        setTeamStatsErrorStatus(err?.response?.status ?? 500);
        setTeamStatsData(null);
      } finally {
        setTeamStatsLoading(false);
      }
    };
    fetchTeamStats();
  }, [activeTab, sportsCode, matchId, teamcode]);

  // Fetch rosters when Rosters tab is active
  useEffect(() => {
    if (activeTab !== "rosters") return;
    const fetchRosters = async () => {
      setRostersLoading(true);
      setRostersError(null);
      setRostersErrorStatus(null);
      try {
        const resp = await api.get(GAME_ROSTERS, {
          params: {
            SportCode: sportsCode?.toUpperCase(),
            Match_id: matchId,
            HomeTeamCode: teamcode,
            ShowStartersOnly: showStartersOnly,
          },
        });
        setRostersData(resp.data);
        const statusMap: Record<string, boolean> = {};
        resp.data.home_team_rosters.forEach((p: any) => {
          statusMap[p.player_id] = p.starter;
        });
        resp.data.opp_team_rosters.forEach((p: any) => {
          statusMap[p.player_id] = p.starter;
        });
        setRostersStatus(statusMap);
      } catch (err: any) {
        setRostersError("Failed to load rosters.");
        setRostersErrorStatus(err?.response?.status ?? 500);
        setRostersData(null);
      } finally {
        setRostersLoading(false);
      }
    };
    fetchRosters();
  }, [activeTab, sportsCode, matchId, teamcode, showStartersOnly]);

  useEffect(() => {
    if (activeTab !== "play-by-play") return;

    const fetchPlayByPlay = async () => {
      setPlayByPlayLoading(true);
      setPlayByPlayError(null);
      setPlayByPlayErrorStatus(null);

      try {
        const resp = await api.get(GAME_PLAY_BY_PLAY, {
          params: {
            SportCode: sportsCode?.toUpperCase(),
            Match_id: matchId,
            HomeTeamCode: teamcode,
          },
        });

        const responseData = resp.data as PlayByPlayResponse;
        console.log('Play-by-play response:', resp.data);
        const playByPlay = responseData.play_by_play || [];
        setPlayByPlayData(playByPlay);
        // clock_display_message is in each row (same value for all rows due to window function)
        // Check first row to determine whether to show/hide clock
        const clockMsg = playByPlay[0]?.clock_display_message || 'show_clock';
        console.log('clock_display_message from first row:', clockMsg);
        setClockDisplayMessage(clockMsg);
      } catch (err: any) {
        setPlayByPlayError("No play-by-play data available for this game.");
        setPlayByPlayErrorStatus(err?.response?.status ?? 500);
        setPlayByPlayData([]);
      } finally {
        setPlayByPlayLoading(false);
      }
    };

    fetchPlayByPlay();
  }, [activeTab, sportsCode, matchId, teamcode]);

  // Fetch game summary/story when Story tab is active
  useEffect(() => {
    if (activeTab !== "summary") return;

    const fetchStory = async () => {
      setStoryLoading(true);
      setStoryError(null);
      setStoryErrorStatus(null);

      try {
        const resp = await api.post(GAME_SUMMARY, {
          SportCode: sportsCode?.toUpperCase(),
          Match_id: matchId,
          HomeTeamCode: teamcode,
        });

        const gameSummary: GameSummary = resp.data;
        setStoryData(gameSummary);
      } catch (err: any) {
        setStoryError("No game summary available for this game.");
        setStoryErrorStatus(err?.response?.status ?? 500);
        setStoryData(null);
      } finally {
        setStoryLoading(false);
      }
    };

    fetchStory();
  }, [activeTab, sportsCode, matchId, teamcode]);

  const formatClockMMSS = (clock: string): string => {
    if (!clock) return "";

    const parts = clock.split(/[.:]/).filter(Boolean);

    if (parts.length === 3) {
      const minutes = parts[1];
      const seconds = parts[2];
      return `${minutes}:${seconds}`;
    }

    if (parts.length === 2) {
      return `${parts[0]}:${parts[1]}`;
    }

    return clock;
  };

  const getActionIcon = (actionType: string, clock: string) => {
    if (clock === "15:00") {
      return "⏱️";
    } else {
      return "⚡";
    }
  };

  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const numericColumns = ['time', 'period'];

  const handleSort = (column: string) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection('asc');
    } else {
      if (numericColumns.includes(column)) {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      } else {
        setSortDirection(prev =>
          prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none'
        );
      }
    }
  };

  const handleSortDirection = (column: string, direction: 'asc' | 'desc') => {
    if (sortColumn === column && sortDirection === direction) {
      setSortColumn(null);
      setSortDirection('none');
    } else {
      setSortColumn(column);
      setSortDirection(direction);
    }
  };

  
  const toSeconds = (clock: string): number => {
    if (!clock) return 0;

    // Handle ISO 8601 duration format: "PT12M34.00S" or "PT05M00.00S"
    const isoMatch = clock.match(/PT(?:(\d+)M)?(?:([\d.]+)S)?/);
    if (isoMatch) {
      const minutes = parseInt(isoMatch[1] ?? '0');
      const seconds = parseFloat(isoMatch[2] ?? '0');
      return minutes * 60 + seconds;
    }

    // Handle standard "MM:SS" format
    const colonParts = clock.split(':');
    if (colonParts.length === 2) {
      return parseInt(colonParts[0]) * 60 + parseInt(colonParts[1]);
    }

    // Handle dotted format: "0.12.34.00" (as used in formatClockMMSS)
    const dottedParts = clock.split(/[.:]/).filter(Boolean);
    if (dottedParts.length === 3) {
      return parseInt(dottedParts[1]) * 60 + parseInt(dottedParts[2]);
    }

    // Fallback: try parsing as a plain number
    return parseFloat(clock) || 0;
  };

  const sortedPlayByPlay = React.useMemo(() => {
    if (!sortColumn || sortDirection === 'none') return playByPlayData;

    return [...playByPlayData].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortColumn) {
        case 'time':
          aVal = toSeconds(a.clock ?? '0');
          bVal = toSeconds(b.clock ?? '0');
          break;

        case 'period':
          aVal = a.period ?? 0;
          bVal = b.period ?? 0;
          break;

        default:
          return 0;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [playByPlayData, sortColumn, sortDirection]);

  const tabs: { tabName: string, tabCode: TabCode }[] = [
    { tabName: "Player Stats", tabCode: "player-stats" },
    { tabName: "Team Stats", tabCode: "team-stats" },
    { tabName: "Rosters", tabCode: "rosters" },
    { tabName: "Play-by-Play", tabCode: "play-by-play" },
    { tabName: "Summary", tabCode: "summary" },
  ];

  const handleTabChange = (tabCode: TabCode) => {
    setActiveTab(tabCode);
  };

  const getTeamLogo = (teamCode: number) => {
    return `https://premier.athlytesports.com/assets/images/teams/${teamCode}.png`;
  };

  const getFallbackGameData = () => ({
    game_date: "Unknown",
    team_name: "Home Team",
    team_code: 0,
    opponent_team_name: "Away Team", 
    opponent_team_code: 0,
    team_score: 0,
    opponent_score: 0,
    venue: "Unknown Venue",
    attendance: 0,
    season: new Date().getFullYear(),
    categories: ["General"]
  });

  const displayGameData = gameData || getFallbackGameData();
  const hasGameDataError = error && !gameData;

  if (isLoading) {
    return (
      <div className="overflow-x-auto w-full flex flex-col">
        <div 
          style={{
            background: `${team?.colorCode || '#1a202c'} url('/playerbanner.png') center/cover no-repeat`,
          }}
        >
          <div className="flex flex-col w-full px-[54px] pt-6">
            <div className="flex items-center justify-between py-4 text-white">
              <div className="flex flex-col items-center w-1/3">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded mb-2 animate-pulse"></div>
                <div className="text-center">
                  <div className="text-lg font-semibold">Loading...</div>
                </div>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="text-4xl font-bold">- - -</div>
              </div>
              
              <div className="flex flex-col items-center w-1/3">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded mb-2 animate-pulse"></div>
                <div className="text-center">
                  <div className="text-lg font-semibold">Loading...</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 text-sm text-white bg-black bg-opacity-20 p-2 rounded-lg">
              <div className="text-center">
                <div className="font-semibold">VENUE</div>
                <div>Loading...</div>
              </div>
              <div className="text-center">
                <div className="font-semibold">ATTENDANCE</div>
                <div>Loading...</div>
              </div>
              <div className="text-center">
                <div className="font-semibold">DATE</div>
                <div>Loading...</div>
              </div>
              <div className="text-center">
                <div className="font-semibold">SEASON</div>
                <div>Loading...</div>
              </div>
            </div>

            <div className="flex mt-6">
              {tabs.map((tab) => (
                <div key={tab.tabCode}>
                  <button
                    onClick={() => handleTabChange(tab.tabCode)}
                    className={`px-4 py-2 transition-all duration-300 text-white font-primary font-normal text-xs ${
                      activeTab === tab.tabCode 
                        ? 'border-b-2 border-white' 
                        : 'hover:bg-gray-300 hover:bg-opacity-20'
                    }`}
                  >
                    {tab.tabName.toUpperCase()}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-8 py-6 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto w-full flex flex-col">
      <div 
        style={{
          background: `${team?.colorCode || '#1a202c'} url('/playerbanner.png') center/cover no-repeat`,
        }}
      >
        <div className="flex flex-col w-full px-[54px] pt-6">
          {hasGameDataError && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded text-sm">
              <div className="flex items-center justify-between">
                <span>
                  {errorStatus && errorStatus >= 500 
                    ? "Server error loading game details. " 
                    : "Failed to load game details. "
                  }
                  Some information may be unavailable.
                </span>
                {errorStatus && errorStatus >= 500 && (
                  <button
                    className="underline text-blue-600 hover:text-blue-800 ml-2"
                    onClick={() =>
                      handleReportIssue(
                        `Failed to fetch game dashboard data (Match ID: ${matchId}, SportCode: ${sportsCode}, TeamCode: ${teamcode}, HTTP ${errorStatus})`
                      )
                    }
                  >
                    Report Issue
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Teams and Scores */}
          <div className="flex items-center justify-between py-4 text-white">
            {/* Home Team */}
            <div className="flex flex-col items-center w-1/3">
              {displayGameData.team_code > 0 && (
                <img 
                  src={getTeamLogo(displayGameData.team_code)} 
                  alt={displayGameData.team_name}
                  className="w-16 h-16 object-contain mb-2"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div className="text-center">
                <div className="text-lg font-semibold">{displayGameData.team_name}</div>
              </div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="text-4xl font-bold">
                {hasGameDataError ? "- -" : `${displayGameData.team_score} - ${displayGameData.opponent_score}`}
              </div>
            </div>
            
            {/* Away Team */}
            <div className="flex flex-col items-center w-1/3">
              {displayGameData.opponent_team_code > 0 && (
                <img 
                  src={getTeamLogo(displayGameData.opponent_team_code)} 
                  alt={displayGameData.opponent_team_name}
                  className="w-16 h-16 object-contain mb-2"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div className="text-center">
                <div className="text-lg font-semibold">{displayGameData.opponent_team_name}</div>
              </div>
            </div>
          </div>
          
          {/* Game Details */}
          <div className="grid grid-cols-4 gap-4 text-sm text-white bg-black bg-opacity-20 p-2 rounded-lg">
            <div className="text-center">
              <div className="font-semibold">VENUE</div>
              <div className="truncate">{displayGameData.venue}</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">ATTENDANCE</div>
              <div>{hasGameDataError ? "N/A" : displayGameData.attendance.toLocaleString()}</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">DATE</div>
              <div>{hasGameDataError ? "N/A" : formatDate(displayGameData.game_date)}</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">SEASON</div>
              <div>{displayGameData.season}</div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex mt-6">
            {tabs.map((tab) => (
              <div key={tab.tabCode}>
                <button
                  onClick={() => handleTabChange(tab.tabCode)}
                  className={`px-4 py-2 transition-all duration-300 text-white font-primary font-normal text-xs ${
                    activeTab === tab.tabCode 
                      ? 'border-b-2 border-white' 
                      : 'hover:bg-gray-300 hover:bg-opacity-20'
                  }`}
                >
                  {tab.tabName.toUpperCase()}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-8 py-6">
        {activeTab === "player-stats" && (
          <div className="p-4 bg-white shadow-md rounded-md overflow-x-auto">
            <h2 className="text-xl font-semibold mb-4">Player Statistics</h2>
            
            {hasGameDataError && (
              <div className="mb-4 bg-yellow-50 text-yellow-800 p-3 rounded text-sm">
                Player stats may be unavailable due to game data loading issues.
              </div>
            )}

            <div className="mb-4">
              <label className="mr-2 font-medium">Category:</label>
              <select
                className="border rounded px-2 py-1"
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                disabled={hasGameDataError}
              >
                {displayGameData?.categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {statsError && statsErrorStatus && statsErrorStatus >= 500 ? (
              <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
                Server Error: Please{" "}
                <button
                  className="underline text-blue-600 hover:text-blue-800"
                  onClick={() =>
                    handleReportIssue(
                      `Failed to fetch player stats (Match ID: ${matchId}, SportCode: ${sportsCode}, TeamCode: ${teamcode}, Category: ${selectedCategory}, HTTP ${statsErrorStatus})`
                    )
                  }
                >
                  click here to report this issue
                </button>
              </div>
            ) : statsLoading ? (
              <div className="text-center py-4">Loading player stats...</div>
            ) : statsError ? (
              <div className="text-red-500 py-4">{statsError}</div>
            ) : statsData && selectedCategory && !hasGameDataError ? (
              <div className="grid grid-cols-1 gap-6">
                <div className="overflow-x-auto">
                  <div className="min-w-full">
                    <PlayerTeamStatsTable
                      teamName={displayGameData.team_name}
                      teamCode={displayGameData.team_code}
                      playerStats={statsData.home_team_player_stats}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <div className="min-w-full">
                    <PlayerTeamStatsTable
                      teamName={displayGameData.opponent_team_name}
                      teamCode={displayGameData.opponent_team_code}
                      playerStats={statsData.opp_team_player_stats}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500 py-4">
                {hasGameDataError 
                  ? "Player stats unavailable due to loading issues." 
                  : "Select a category to view player stats."}
              </div>
            )}
          </div>
        )}

        {activeTab === "team-stats" && (
          <div className="p-4 bg-white rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Team Statistics</h2>
            
            {hasGameDataError && (
              <div className="mb-4 bg-yellow-50 text-yellow-800 p-3 rounded text-sm">
                Team stats may be unavailable due to game data loading issues.
              </div>
            )}

            {teamStatsError && teamStatsErrorStatus && teamStatsErrorStatus >= 500 ? (
              <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
                Server Error: Please{" "}
                <button
                  className="underline text-blue-600 hover:text-blue-800"
                  onClick={() =>
                    handleReportIssue(
                      `Failed to fetch team stats (Match ID: ${matchId}, SportCode: ${sportsCode}, TeamCode: ${teamcode}, HTTP ${teamStatsErrorStatus})`
                    )
                  }
                >
                  click here to report this issue
                </button>
              </div>
            ) : teamStatsLoading ? (
              <div className="text-center py-4">Loading team stats...</div>
            ) : teamStatsError ? (
              <div className="text-red-500 py-4">{teamStatsError}</div>
            ) : teamStatsData && !hasGameDataError ? (
              <TeamStatsTable
                homeTeamName={teamStatsData?.home_team_name}
                oppTeamName={teamStatsData?.opp_team_name}
                homeStats={teamStatsData?.home_team_stats.reduce((acc: any, s: any) => ({ ...acc, ...s.stats }), {})}
                oppStats={teamStatsData?.opp_team_stats.reduce((acc: any, s: any) => ({ ...acc, ...s.stats }), {})}
                homeTeamCode={displayGameData.team_code}
                oppTeamCode={displayGameData.opponent_team_code}
              />
            ) : (
              <div className="text-gray-500 py-4">
                {hasGameDataError 
                  ? "Team stats unavailable due to loading issues." 
                  : "No team stats available."}
              </div>
            )}
          </div>
        )}

        {activeTab === "rosters" && (
          <div className="p-4 bg-white rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Team Rosters</h2>
            
            {hasGameDataError && (
              <div className="mb-4 bg-yellow-50 text-yellow-800 p-3 rounded text-sm">
                Rosters may be unavailable due to game data loading issues.
              </div>
            )}

            <div className="flex items-center mb-4">
              <label className="mr-2 text-sm font-medium">Show:</label>
              <select
                value={showStartersOnly ? 'starters' : 'all'}
                onChange={e => setShowStartersOnly(e.target.value === 'starters')}
                className="border rounded px-2 py-1 text-sm"
                disabled={hasGameDataError}
              >
                <option value="all">All Players</option>
                <option value="starters">Starters Only</option>
              </select>
            </div>

            {rostersError && rostersErrorStatus && rostersErrorStatus >= 500 ? (
              <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
                Server Error: Please{" "}
                <button
                  className="underline text-blue-600 hover:text-blue-800"
                  onClick={() =>
                    handleReportIssue(
                      `Failed to fetch rosters (Match ID: ${matchId}, SportCode: ${sportsCode}, TeamCode: ${teamcode}, ShowStartersOnly: ${showStartersOnly}, HTTP ${rostersErrorStatus})`
                    )
                  }
                >
                  click here to report this issue
                </button>
              </div>
            ) : rostersLoading ? (
              <div className="text-center py-4">Loading rosters...</div>
            ) : rostersError ? (
              <div className="text-red-500 py-4">{rostersError}</div>
            ) : rostersData && !hasGameDataError ? (
              <RostersTable
                homeRosters={rostersData.home_team_rosters}
                oppRosters={rostersData.opp_team_rosters}
                homeTeamName={displayGameData.team_name}
                homeTeamCode={displayGameData.team_code}
                oppTeamName={displayGameData.opponent_team_name}
                oppTeamCode={displayGameData.opponent_team_code}
              />
            ) : (
              <div className="text-gray-500 py-4">
                {hasGameDataError 
                  ? "Rosters unavailable due to loading issues." 
                  : "No roster data available."}
              </div>
            )}
          </div>
        )}

        {activeTab === "play-by-play" && (
          <div className="p-4 bg-white rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Play By Play</h2>

            {playByPlayError && playByPlayErrorStatus && playByPlayErrorStatus >= 500 ? (
              <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
                Server Error: Please{" "}
                <button
                  className="underline text-blue-600 hover:text-blue-800"
                  onClick={() =>
                    handleReportIssue(
                      `Failed to fetch play-by-play (Match ID: ${matchId}, SportCode: ${sportsCode}, HTTP ${playByPlayErrorStatus})`
                    )
                  }
                >
                  click here to report this issue
                </button>
              </div>
            ) : playByPlayLoading ? (
              <div className="text-center py-4">Loading play-by-play...</div>
            ) : playByPlayError ? (
              <div className="text-gray-500 py-4">{playByPlayError}</div>
            ) : playByPlayData.length > 0 ? (
              <div className="space-y-3">
                <div className={`grid gap-x-4 border-b pb-2 ${clockDisplayMessage === 'show_clock' ? 'grid-cols-[100px_80px_120px_1fr_80px_80px]' : 'grid-cols-[80px_120px_1fr_80px_80px]'}`}>
                  {clockDisplayMessage === 'show_clock' && (
                    <button
                      onClick={() => numericColumns.includes('time') && handleSort('time')}
                      className="text-sm text-gray-500 text-left hover:text-gray-800 font-semibold flex items-center gap-1 select-none"
                    >
                      TIME
                      {numericColumns.includes('time') && (
                        <span className="flex flex-col ml-1" style={{ lineHeight: '8px' }}>
                          <span
                            style={{
                              fontSize: '8px',
                              opacity: sortColumn === 'time' && sortDirection === 'asc' ? 1 : 0.3,
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSortDirection('time', 'asc');
                            }}
                          >
                            ▲
                          </span>
                          <span
                            style={{
                              fontSize: '8px',
                              opacity: sortColumn === 'time' && sortDirection === 'desc' ? 1 : 0.3,
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSortDirection('time', 'desc');
                            }}
                          >
                            ▼
                          </span>
                        </span>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => numericColumns.includes('action') && handleSort('action')}
                    className="text-sm text-gray-500 text-left hover:text-gray-800 font-semibold flex items-center gap-1 select-none"
                  >
                    ACTION
                  </button>
                  <button
                    onClick={() => numericColumns.includes('team') && handleSort('team')}
                    className="text-sm text-gray-500 text-left hover:text-gray-800 font-semibold flex items-center gap-1 select-none"
                  >
                    TEAM
                  </button>
                  <button
                    onClick={() => numericColumns.includes('text') && handleSort('text')}
                    className="text-sm text-gray-500 text-left hover:text-gray-800 font-semibold flex items-center gap-1 select-none"
                  >
                    DESCRIPTION
                  </button>
                  <button
                    onClick={() => numericColumns.includes('period') && handleSort('period')}
                    className="text-sm text-gray-500 text-left hover:text-gray-800 font-semibold flex items-center gap-1 select-none"
                  >
                    PERIOD
                    {numericColumns.includes('period') && (
                      <span className="flex flex-col ml-1" style={{ lineHeight: '8px' }}>
                        <span
                          style={{
                            fontSize: '8px',
                            opacity: sortColumn === 'period' && sortDirection === 'asc' ? 1 : 0.3,
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSortDirection('period', 'asc');
                          }}
                        >
                          ▲
                        </span>
                        <span
                          style={{
                            fontSize: '8px',
                            opacity: sortColumn === 'period' && sortDirection === 'desc' ? 1 : 0.3,
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSortDirection('period', 'desc');
                          }}
                        >
                          ▼
                        </span>
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => numericColumns.includes('type') && handleSort('type')}
                    className="text-sm text-gray-500 text-left hover:text-gray-800 font-semibold flex items-center gap-1 select-none"
                  >
                    TYPE
                  </button>
                </div>

                {sortedPlayByPlay.map((event) => (
                  <div
                    key={event.id}
                    className={`grid gap-x-4 border-b pb-2 ${clockDisplayMessage === 'show_clock' ? 'grid-cols-[100px_80px_120px_1fr_80px_80px]' : 'grid-cols-[80px_120px_1fr_80px_80px]'}`}
                  >
                    {clockDisplayMessage === 'show_clock' && (
                      <div className="text-sm text-gray-500">{formatClockMMSS(event.clock)}'</div>
                    )}
                    <div className="text-lg">{getActionIcon(event.action_type, formatClockMMSS(event.clock))}</div>
                    <div className="text-sm font-medium">{event.team_name}</div>
                    <div className="text-sm">{event.text}</div>
                    <div className="text-sm">{event.period}</div>
                    <div className="text-sm">{event.period_type}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 py-4">No play-by-play data available.</div>
            )}
          </div>
        )}

        {activeTab === "summary" && (
          <div className="p-4 bg-white rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Game Summary</h2>

            {storyError && storyErrorStatus && storyErrorStatus >= 500 ? (
              <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
                Server Error: Please{" "}
                <button
                  className="underline text-blue-600 hover:text-blue-800"
                  onClick={() =>
                    handleReportIssue(
                      `Failed to fetch game summary (Match ID: ${matchId}, SportCode: ${sportsCode}, HTTP ${storyErrorStatus})`
                    )
                  }
                >
                  click here to report this issue
                </button>
              </div>
            ) : storyLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="relative w-12 h-12 mb-4">
                  <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
                </div>
                <p className="text-gray-600 font-medium">Generating game summary...</p>
              </div>
            ) : storyError ? (
              <div className="text-gray-500 py-4">{storyError}</div>
            ) : storyData && storyData.summary ? (
              <div className="prose prose-sm max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {storyData.summary}
                </div>
              </div>
            ) : (
              <div className="text-gray-500 py-4">No game summary available.</div>
            )}
          </div>
        )}
      </div>

      {/* Report Issue Modal */}
      <ReportIssueModal
        isOpen={reportIssue.isModalOpen}
        onClose={reportIssue.closeModal}
        onSubmit={reportIssue.handleSubmit}
        issue={reportIssue.issue}
        setIssue={reportIssue.setIssue}
        feedbackMessage={reportIssue.feedbackMessage}
        feedbackType={reportIssue.feedbackType}
        isLoading={reportIssue.isLoading}
      />
    </div>
  );
};

export default GameDashboard;